import React, { useState } from 'react';
import { TrendingUpIcon, DownloadIcon, CheckCircleIcon, ActivityIcon, TargetIcon, DollarSignIcon, UsersIcon, ZapIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpIcon, AlertCircleIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend, Cell } from 'recharts';
type Period = 'month' | 'quarter' | 'fy';
type Scenario = 'base' | 'push' | 'downside';
interface ProgramForecast {
  id: string;
  name: string;
  lifecycle: string[];
  reach: number;
  engagementRate: number;
  meetingRate: number;
  oppRate: number;
  acv: number;
  expansionAcv: number;
  expansionRate?: number;
  aiAssist: boolean;
  calculatedPipeline: number;
  calculatedExpansion: number;
  attainmentImpact: number;
  confidence: number;
}
export function ForecastingPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('quarter');
  const [scenario, setScenario] = useState<Scenario>('base');
  const [showAllPrograms, setShowAllPrograms] = useState(false);
  // Targets
  const targets = {
    pipeline: 2800000,
    expansion: 650000,
    meetings: 250
  };
  // 15 Program forecasts with editable assumptions
  const [programs, setPrograms] = useState<ProgramForecast[]>([{
    id: '1',
    name: 'In-Market Account Acceleration',
    lifecycle: ['Acquire', 'Close'],
    reach: 500,
    engagementRate: 20,
    meetingRate: 30,
    oppRate: 20,
    acv: 18000,
    expansionAcv: 0,
    aiAssist: true,
    calculatedPipeline: 180000,
    calculatedExpansion: 0,
    attainmentImpact: 6.4,
    confidence: 85
  }, {
    id: '2',
    name: 'Event-Driven Pipeline',
    lifecycle: ['Acquire', 'Close'],
    reach: 300,
    engagementRate: 25,
    meetingRate: 40,
    oppRate: 18,
    acv: 25000,
    expansionAcv: 0,
    aiAssist: true,
    calculatedPipeline: 135000,
    calculatedExpansion: 0,
    attainmentImpact: 4.8,
    confidence: 88
  }, {
    id: '3',
    name: 'Seasonal & Trigger-Based Campaigns',
    lifecycle: ['Acquire'],
    reach: 400,
    engagementRate: 18,
    meetingRate: 22,
    oppRate: 16,
    acv: 16000,
    expansionAcv: 0,
    aiAssist: false,
    calculatedPipeline: 101376,
    calculatedExpansion: 0,
    attainmentImpact: 3.6,
    confidence: 72
  }, {
    id: '4',
    name: 'High-Value Content Syndication Engine',
    lifecycle: ['Acquire', 'Nurture'],
    reach: 520,
    engagementRate: 23,
    meetingRate: 18,
    oppRate: 12,
    acv: 14000,
    expansionAcv: 0,
    aiAssist: true,
    calculatedPipeline: 183686,
    calculatedExpansion: 0,
    attainmentImpact: 6.6,
    confidence: 78
  }, {
    id: '5',
    name: 'Account Warming & ABM 1-to-1',
    lifecycle: ['Pre-pipeline', 'Acquire'],
    reach: 220,
    engagementRate: 32,
    meetingRate: 20,
    oppRate: 15,
    acv: 21000,
    expansionAcv: 0,
    aiAssist: true,
    calculatedPipeline: 44352,
    calculatedExpansion: 0,
    attainmentImpact: 1.6,
    confidence: 82
  }, {
    id: '6',
    name: 'Opportunity Re-Engagement',
    lifecycle: ['Close'],
    reach: 200,
    engagementRate: 30,
    meetingRate: 25,
    oppRate: 22,
    acv: 20000,
    expansionAcv: 0,
    aiAssist: false,
    calculatedPipeline: 66000,
    calculatedExpansion: 0,
    attainmentImpact: 2.4,
    confidence: 75
  }, {
    id: '7',
    name: 'Competitive Displacement Motion',
    lifecycle: ['Close'],
    reach: 160,
    engagementRate: 24,
    meetingRate: 26,
    oppRate: 20,
    acv: 27000,
    expansionAcv: 0,
    aiAssist: true,
    calculatedPipeline: 53913,
    calculatedExpansion: 0,
    attainmentImpact: 1.9,
    confidence: 80
  }, {
    id: '8',
    name: 'Conversion Optimization Sprint',
    lifecycle: ['Mid-funnel'],
    reach: 280,
    engagementRate: 26,
    meetingRate: 24,
    oppRate: 18,
    acv: 19000,
    expansionAcv: 0,
    aiAssist: true,
    calculatedPipeline: 223334,
    calculatedExpansion: 0,
    attainmentImpact: 8.0,
    confidence: 83
  }, {
    id: '9',
    name: 'Product-Led Expansion',
    lifecycle: ['Expand'],
    reach: 180,
    engagementRate: 28,
    expansionRate: 15,
    oppRate: 0,
    acv: 0,
    expansionAcv: 22000,
    aiAssist: true,
    calculatedPipeline: 0,
    calculatedExpansion: 166320,
    attainmentImpact: 0,
    confidence: 86
  }, {
    id: '10',
    name: 'Account Expansion - AI Products',
    lifecycle: ['Expand'],
    reach: 140,
    engagementRate: 30,
    expansionRate: 16,
    oppRate: 0,
    acv: 0,
    expansionAcv: 23000,
    aiAssist: true,
    calculatedPipeline: 0,
    calculatedExpansion: 154560,
    attainmentImpact: 0,
    confidence: 84
  }, {
    id: '11',
    name: 'Champion Activation',
    lifecycle: ['Close', 'Expand'],
    reach: 150,
    engagementRate: 35,
    meetingRate: 28,
    oppRate: 19,
    acv: 24000,
    expansionAcv: 0,
    aiAssist: false,
    calculatedPipeline: 66780,
    calculatedExpansion: 0,
    attainmentImpact: 2.4,
    confidence: 77
  }, {
    id: '12',
    name: 'Win-Back & Renewals Save',
    lifecycle: ['Retain', 'Regain'],
    reach: 140,
    engagementRate: 27,
    meetingRate: 24,
    oppRate: 18,
    acv: 19000,
    expansionAcv: 0,
    aiAssist: false,
    calculatedPipeline: 51926,
    calculatedExpansion: 0,
    attainmentImpact: 1.9,
    confidence: 70
  }, {
    id: '13',
    name: 'Partner-Assisted ABM',
    lifecycle: ['Acquire', 'Close'],
    reach: 190,
    engagementRate: 25,
    meetingRate: 23,
    oppRate: 17,
    acv: 20000,
    expansionAcv: 0,
    aiAssist: true,
    calculatedPipeline: 37145,
    calculatedExpansion: 0,
    attainmentImpact: 1.3,
    confidence: 68
  }, {
    id: '14',
    name: 'Regional & Field Motion Accelerator',
    lifecycle: ['Acquire', 'Close'],
    reach: 210,
    engagementRate: 24,
    meetingRate: 32,
    oppRate: 19,
    acv: 21000,
    expansionAcv: 0,
    aiAssist: true,
    calculatedPipeline: 64166,
    calculatedExpansion: 0,
    attainmentImpact: 2.3,
    confidence: 81
  }, {
    id: '15',
    name: 'Predictive Priority & Signal Amplifier',
    lifecycle: ['Acquire', 'Close'],
    reach: 260,
    engagementRate: 29,
    meetingRate: 31,
    oppRate: 21,
    acv: 23000,
    expansionAcv: 0,
    aiAssist: true,
    calculatedPipeline: 113518,
    calculatedExpansion: 0,
    attainmentImpact: 4.1,
    confidence: 89
  }]);
  // Calculate totals
  const totalPipeline = programs.reduce((sum, p) => sum + p.calculatedPipeline, 0);
  const totalExpansion = programs.reduce((sum, p) => sum + p.calculatedExpansion, 0);
  const totalPipelineWithAI = programs.filter(p => p.aiAssist).reduce((sum, p) => sum + p.calculatedPipeline, 0);
  const totalMeetings = 232;
  const pipelineAttainment = totalPipeline / targets.pipeline * 100;
  const expansionAttainment = totalExpansion / targets.expansion * 100;
  const meetingsAttainment = totalMeetings / targets.meetings * 100;
  const programsOnPace = programs.filter(p => {
    const total = p.calculatedPipeline + p.calculatedExpansion;
    return total >= 50000;
  }).length;
  const hasAIAssist = programs.some(p => p.aiAssist);
  // Scenario data (weeks in quarter)
  const generateScenarioData = () => {
    const weeks = 13;
    const baseGrowth = totalPipeline / weeks;
    const pushMultiplier = 1.25;
    const downsideMultiplier = 0.75;
    return Array.from({
      length: weeks
    }, (_, i) => ({
      week: `W${i + 1}`,
      base: Math.round(baseGrowth * (i + 1)),
      push: Math.round(baseGrowth * (i + 1) * pushMultiplier),
      downside: Math.round(baseGrowth * (i + 1) * downsideMultiplier)
    }));
  };
  const scenarioData = generateScenarioData();
  // Trend sparkline data (last 14 days)
  const generateSparklineData = (baseValue: number, variance: number) => {
    return Array.from({
      length: 14
    }, (_, i) => ({
      day: i + 1,
      value: Math.round(baseValue + (Math.random() - 0.5) * variance)
    }));
  };
  const meetingsData = generateSparklineData(18, 6);
  const orchestrationsData = generateSparklineData(45, 15);
  const aiPlaysData = generateSparklineData(32, 10);
  const updateProgram = (id: string, field: keyof ProgramForecast, value: number | boolean) => {
    setPrograms(programs.map(p => {
      if (p.id !== id) return p;
      const updated = {
        ...p,
        [field]: value
      };
      const reach = updated.reach;
      const engaged = reach * (updated.engagementRate / 100);
      if (updated.expansionRate) {
        const expanded = engaged * (updated.expansionRate / 100);
        updated.calculatedExpansion = updated.expansionAcv > 0 ? expanded * updated.expansionAcv : 0;
        updated.calculatedPipeline = 0;
      } else {
        const meetings = engaged * (updated.meetingRate / 100);
        const opps = meetings * (updated.oppRate / 100);
        updated.calculatedPipeline = updated.acv > 0 ? opps * updated.acv : 0;
        updated.calculatedExpansion = 0;
      }
      updated.attainmentImpact = updated.calculatedPipeline / targets.pipeline * 100;
      return updated;
    }));
  };
  // Top 7 programs for contribution chart
  const top7Programs = [...programs].sort((a, b) => {
    const aTotal = a.calculatedPipeline + a.calculatedExpansion;
    const bTotal = b.calculatedPipeline + b.calculatedExpansion;
    return bTotal - aTotal;
  }).slice(0, 7).map(p => ({
    name: p.name.length > 30 ? p.name.substring(0, 30) + '...' : p.name,
    pipeline: p.calculatedPipeline,
    expansion: p.calculatedExpansion,
    total: p.calculatedPipeline + p.calculatedExpansion,
    onPace: p.calculatedPipeline + p.calculatedExpansion >= 50000,
    id: p.id
  }));
  // Expansion programs for waterfall
  const expansionPrograms = programs.filter(p => p.lifecycle.includes('Expand') || p.lifecycle.includes('Retain') || p.lifecycle.includes('Regain'));
  const sortedPrograms = [...programs].sort((a, b) => {
    const aTotal = a.calculatedPipeline + a.calculatedExpansion;
    const bTotal = b.calculatedPipeline + b.calculatedExpansion;
    return bTotal - aTotal;
  });
  const displayedPrograms = showAllPrograms ? sortedPrograms : sortedPrograms.slice(0, 7);
  const potentialLevers = [{
    action: 'Increase reach',
    name: 'In-Market Account Acceleration to 650',
    impact: '~65k pipeline',
    programId: '1',
    estimatedImpact: 65000,
    onClick: () => navigate('/programs/1')
  }, {
    action: 'Turn on orchestration',
    name: 'Opportunity Re-Engagement',
    impact: 'reclaim stalled opps faster',
    programId: '6',
    estimatedImpact: 45000,
    onClick: () => navigate('/programs/6')
  }, {
    action: 'Activate',
    name: 'Account Expansion - AI Products',
    impact: '~70k expansion',
    programId: '10',
    estimatedImpact: 70000,
    onClick: () => navigate('/programs/10')
  }, {
    action: 'Add playbook',
    name: 'Intent-Based Engagement',
    impact: '~55k pipeline',
    estimatedImpact: 55000,
    onClick: () => navigate('/playbooks')
  }, {
    action: 'Connect',
    name: 'Partner Portals',
    impact: 'unlock Partner-Assisted ABM',
    programId: '13',
    estimatedImpact: 50000,
    onClick: () => navigate('/partner-portals')
  }];
  const topLevers = potentialLevers.sort((a, b) => b.estimatedImpact - a.estimatedImpact).slice(0, 3);
  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    return `$${(value / 1000).toFixed(0)}K`;
  };
  return <div className="w-full min-h-screen bg-gray-50" data-id="element-4781">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4" data-id="element-4782">
        <div className="flex items-center justify-between" data-id="element-4783">
          <div data-id="element-4784">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-4785">Forecast</h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-4786">
              Revenue attainment and scenario modeling across 15 GTM programs
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-4787">
            <select value={period} onChange={e => setPeriod(e.target.value as Period)} className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-4788">
              <option value="month" data-id="element-4789">This Month</option>
              <option value="quarter" data-id="element-4790">This Quarter</option>
              <option value="fy" data-id="element-4791">This FY</option>
            </select>
            <select value={scenario} onChange={e => setScenario(e.target.value as Scenario)} className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-4792">
              <option value="base" data-id="element-4793">Base Scenario</option>
              <option value="push" data-id="element-4794">Push Scenario</option>
              <option value="downside" data-id="element-4795">Downside Scenario</option>
            </select>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm" data-id="element-4796">
              <DownloadIcon className="w-4 h-4" data-id="element-4797" />
              Export
            </button>
          </div>
        </div>
      </div>
      {/* Main Content - 2 Column Layout */}
      <div className="flex gap-6 p-6" data-id="element-4798">
        {/* Left Column - Calculator & Charts */}
        <div className="flex-1 space-y-6" data-id="element-4799">
          {/* Forecast Calculator */}
          <div className="bg-white rounded-lg border border-gray-200" data-id="element-4800">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-4801">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-4802">
                Forecast Calculator
              </h2>
              <p className="text-sm text-gray-600 mt-1" data-id="element-4803">
                Adjust assumptions to update attainment (showing 6 at a time,
                scroll for all 15)
              </p>
            </div>
            <div className="p-6" data-id="element-4804">
              <div className="overflow-x-auto" data-id="element-4805">
                <div className="max-h-96 overflow-y-auto" data-id="element-4806">
                  <table className="w-full" data-id="element-4807">
                    <thead className="sticky top-0 bg-white" data-id="element-4808">
                      <tr className="border-b border-gray-200" data-id="element-4809">
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase" data-id="element-4810">
                          Program
                        </th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase" data-id="element-4811">
                          Reach
                        </th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase" data-id="element-4812">
                          Engage %
                        </th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase" data-id="element-4813">
                          Convert %
                        </th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase" data-id="element-4814">
                          Opp %
                        </th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase" data-id="element-4815">
                          ACV
                        </th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase" data-id="element-4816">
                          AI
                        </th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase" data-id="element-4817">
                          Output
                        </th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600 uppercase" data-id="element-4818">
                          Impact
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100" data-id="element-4819">
                      {programs.map(program => <tr key={program.id} className="hover:bg-gray-50" data-id="element-4820">
                          <td className="py-3 px-3" data-id="element-4821">
                            <div data-id="element-4822">
                              <span className="text-sm font-medium text-gray-900" data-id="element-4823">
                                {program.name}
                              </span>
                              <div className="flex items-center gap-1 mt-0.5" data-id="element-4824">
                                {program.lifecycle.map((lc, i) => <span key={i} className="text-xs text-gray-500" data-id="element-4825">
                                    {lc}
                                  </span>)}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-3" data-id="element-4826">
                            <input type="number" value={program.reach} onChange={e => updateProgram(program.id, 'reach', Number(e.target.value))} className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-4827" />
                          </td>
                          <td className="py-3 px-3" data-id="element-4828">
                            <input type="number" value={program.engagementRate} onChange={e => updateProgram(program.id, 'engagementRate', Number(e.target.value))} className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-4829" />
                          </td>
                          <td className="py-3 px-3" data-id="element-4830">
                            <input type="number" value={program.expansionRate || program.meetingRate} onChange={e => updateProgram(program.id, program.expansionRate ? 'expansionRate' : 'meetingRate', Number(e.target.value))} className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-4831" />
                          </td>
                          <td className="py-3 px-3" data-id="element-4832">
                            {program.oppRate > 0 ? <input type="number" value={program.oppRate} onChange={e => updateProgram(program.id, 'oppRate', Number(e.target.value))} className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-4833" /> : <span className="text-sm text-gray-400" data-id="element-4834">-</span>}
                          </td>
                          <td className="py-3 px-3" data-id="element-4835">
                            <input type="number" value={program.acv || program.expansionAcv} onChange={e => updateProgram(program.id, program.acv > 0 ? 'acv' : 'expansionAcv', Number(e.target.value))} className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-4836" />
                          </td>
                          <td className="py-3 px-3" data-id="element-4837">
                            <input type="checkbox" checked={program.aiAssist} onChange={e => updateProgram(program.id, 'aiAssist', e.target.checked)} className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500" data-id="element-4838" />
                          </td>
                          <td className="py-3 px-3" data-id="element-4839">
                            <span className="text-sm font-semibold text-gray-900" data-id="element-4840">
                              $
                              {((program.calculatedPipeline + program.calculatedExpansion) / 1000).toFixed(0)}
                              k
                            </span>
                          </td>
                          <td className="py-3 px-3" data-id="element-4841">
                            <span className="text-sm font-semibold text-blue-600" data-id="element-4842">
                              {program.calculatedPipeline > 0 ? program.attainmentImpact.toFixed(1) : (program.calculatedExpansion / targets.expansion * 100).toFixed(1)}
                              %
                            </span>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          {/* Program Contribution Chart */}
          <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4843">
            <div className="mb-4" data-id="element-4844">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-4845">
                Top 7 Programs by Contribution
              </h3>
              <p className="text-sm text-gray-600 mt-1" data-id="element-4846">
                Click any bar to view program details
              </p>
            </div>
            <ResponsiveContainer width="100%" height={300} data-id="element-4847">
              <BarChart data={top7Programs} layout="vertical" margin={{
              top: 5,
              right: 30,
              left: 150,
              bottom: 5
            }} data-id="element-4848">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} data-id="element-4849" />
                <XAxis type="number" tickFormatter={formatCurrency} data-id="element-4850" />
                <YAxis type="category" dataKey="name" width={140} tick={{
                fontSize: 12
              }} data-id="element-4851" />
                <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }} data-id="element-4852" />
                <Bar dataKey="total" radius={[0, 4, 4, 0]} cursor="pointer" onClick={data => navigate(`/programs/${data.id}`)} data-id="element-4853">
                  {top7Programs.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.onPace ? '#3b82f6' : '#9ca3af'} data-id="element-4854" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="flex items-center gap-4 mt-4 text-xs" data-id="element-4855">
              <div className="flex items-center gap-2" data-id="element-4856">
                <div className="w-3 h-3 bg-blue-500 rounded" data-id="element-4857" />
                <span className="text-gray-600" data-id="element-4858">On pace (≥$50K)</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-4859">
                <div className="w-3 h-3 bg-gray-400 rounded" data-id="element-4860" />
                <span className="text-gray-600" data-id="element-4861">Below pace</span>
              </div>
            </div>
          </div>
          {/* Scenario Comparison Chart */}
          <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-4862">
            <div className="mb-4" data-id="element-4863">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-4864">
                Scenario Comparison
              </h3>
              <p className="text-sm text-gray-600 mt-1" data-id="element-4865">
                Pipeline forecast over{' '}
                {period === 'quarter' ? '13 weeks' : period === 'month' ? '4 weeks' : '52 weeks'}
              </p>
            </div>
            <ResponsiveContainer width="100%" height={280} data-id="element-4866">
              <LineChart data={scenarioData} margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5
            }} data-id="element-4867">
                <CartesianGrid strokeDasharray="3 3" data-id="element-4868" />
                <XAxis dataKey="week" data-id="element-4869" />
                <YAxis tickFormatter={formatCurrency} data-id="element-4870" />
                <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }} data-id="element-4871" />
                <Legend data-id="element-4872" />
                <Line type="monotone" dataKey="base" stroke="#3b82f6" strokeWidth={scenario === 'base' ? 3 : 1.5} name="Base Scenario" strokeDasharray={scenario === 'base' ? '0' : '5 5'} data-id="element-4873" />
                <Line type="monotone" dataKey="push" stroke="#10b981" strokeWidth={scenario === 'push' ? 3 : 1.5} name="Push Scenario (+25%)" strokeDasharray={scenario === 'push' ? '0' : '5 5'} data-id="element-4874" />
                <Line type="monotone" dataKey="downside" stroke="#ef4444" strokeWidth={scenario === 'downside' ? 3 : 1.5} name="Downside Scenario (-25%)" strokeDasharray={scenario === 'downside' ? '0' : '5 5'} data-id="element-4875" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {/* Program Contribution Table */}
          <div className="bg-white rounded-lg border border-gray-200" data-id="element-4876">
            <div className="px-6 py-4 border-b border-gray-200" data-id="element-4877">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-4878">
                Program Contribution
              </h3>
              <p className="text-sm text-gray-600 mt-1" data-id="element-4879">
                Ranked by total output
              </p>
            </div>
            <div className="overflow-x-auto" data-id="element-4880">
              <table className="w-full" data-id="element-4881">
                <thead data-id="element-4882">
                  <tr className="border-b border-gray-200" data-id="element-4883">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase" data-id="element-4884">
                      Program
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase" data-id="element-4885">
                      Pipeline
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase" data-id="element-4886">
                      Expansion
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase" data-id="element-4887">
                      Attainment
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase" data-id="element-4888">
                      Confidence
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase" data-id="element-4889">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-4890">
                  {displayedPrograms.map(program => <tr key={program.id} className="hover:bg-gray-50" data-id="element-4891">
                      <td className="py-3 px-4" data-id="element-4892">
                        <span className="text-sm font-medium text-gray-900" data-id="element-4893">
                          {program.name}
                        </span>
                      </td>
                      <td className="py-3 px-4" data-id="element-4894">
                        <span className="text-sm font-semibold text-gray-900" data-id="element-4895">
                          ${(program.calculatedPipeline / 1000).toFixed(0)}k
                        </span>
                      </td>
                      <td className="py-3 px-4" data-id="element-4896">
                        <span className="text-sm font-semibold text-gray-900" data-id="element-4897">
                          ${(program.calculatedExpansion / 1000).toFixed(0)}k
                        </span>
                      </td>
                      <td className="py-3 px-4" data-id="element-4898">
                        <span className="text-sm font-semibold text-blue-600" data-id="element-4899">
                          {program.calculatedPipeline > 0 ? program.attainmentImpact.toFixed(1) : (program.calculatedExpansion / targets.expansion * 100).toFixed(1)}
                          %
                        </span>
                      </td>
                      <td className="py-3 px-4" data-id="element-4900">
                        <div className="flex items-center gap-2" data-id="element-4901">
                          <div className={`w-2 h-2 rounded-full ${program.confidence >= 80 ? 'bg-green-500' : program.confidence >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`} data-id="element-4902" />
                          <span className="text-sm text-gray-900" data-id="element-4903">
                            {program.confidence}%
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4" data-id="element-4904">
                        <button onClick={() => navigate(`/programs/${program.id}`)} className="text-sm text-blue-600 hover:text-blue-700" data-id="element-4905">
                          View
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            {!showAllPrograms && sortedPrograms.length > 7 && <div className="px-6 py-3 border-t border-gray-200" data-id="element-4906">
                <button onClick={() => setShowAllPrograms(true)} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-4907">
                  <ChevronDownIcon className="w-4 h-4" data-id="element-4908" />
                  Show all 15 programs
                </button>
              </div>}
            {showAllPrograms && <div className="px-6 py-3 border-t border-gray-200" data-id="element-4909">
                <button onClick={() => setShowAllPrograms(false)} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-4910">
                  <ChevronUpIcon className="w-4 h-4" data-id="element-4911" />
                  Show top 7 only
                </button>
              </div>}
          </div>
        </div>
        {/* Right Column - Attainment, Data Health, Levers, Sparklines */}
        <div className="w-80 space-y-6" data-id="element-4912">
          {/* Attainment Cards */}
          <div className="space-y-3" data-id="element-4913">
            <h3 className="text-sm font-semibold text-gray-900 px-1" data-id="element-4914">
              Attainment
            </h3>
            {/* Pipeline Attainment with Mini Chart */}
            <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-4915">
              <div className="flex items-center justify-between mb-2" data-id="element-4916">
                <span className="text-xs font-medium text-gray-600" data-id="element-4917">
                  Pipeline attainment
                </span>
                <TargetIcon className="w-4 h-4 text-blue-600" data-id="element-4918" />
              </div>
              <div className="mb-2" data-id="element-4919">
                <span className="text-3xl font-bold text-gray-900" data-id="element-4920">
                  {pipelineAttainment.toFixed(0)}%
                </span>
              </div>
              <div className="text-xs text-gray-600 mb-3" data-id="element-4921">
                ${(totalPipeline / 1000000).toFixed(2)}M of $
                {(targets.pipeline / 1000000).toFixed(1)}M target
              </div>
              {/* Mini Column Chart */}
              <div className="h-20 mb-2" data-id="element-4922">
                <ResponsiveContainer width="100%" height="100%" data-id="element-4923">
                  <BarChart data={[{
                  name: 'Target',
                  value: targets.pipeline
                }, {
                  name: 'Forecast',
                  value: totalPipeline
                }, ...(hasAIAssist ? [{
                  name: 'AI Lift',
                  value: totalPipelineWithAI
                }] : [])]} data-id="element-4924">
                    <Bar dataKey="value" radius={[4, 4, 0, 0]} data-id="element-4925">
                      <Cell fill="#cbd5e1" data-id="element-4926" />
                      <Cell fill="#3b82f6" data-id="element-4927" />
                      {hasAIAssist && <Cell fill="#8b5cf6" data-id="element-4928" />}
                    </Bar>
                    <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{
                    fontSize: '11px'
                  }} data-id="element-4929" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-4930">
                <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full" style={{
                width: `${Math.min(pipelineAttainment, 100)}%`
              }} data-id="element-4931" />
              </div>
            </div>
            {/* Expansion Attainment */}
            <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-4932">
              <div className="flex items-center justify-between mb-2" data-id="element-4933">
                <span className="text-xs font-medium text-gray-600" data-id="element-4934">
                  Expansion attainment
                </span>
                <TrendingUpIcon className="w-4 h-4 text-green-600" data-id="element-4935" />
              </div>
              <div className="mb-2" data-id="element-4936">
                <span className="text-3xl font-bold text-gray-900" data-id="element-4937">
                  {expansionAttainment.toFixed(0)}%
                </span>
              </div>
              <div className="text-xs text-gray-600" data-id="element-4938">
                ${(totalExpansion / 1000).toFixed(0)}k of $
                {(targets.expansion / 1000).toFixed(0)}k target
              </div>
              <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-4939">
                <div className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full" style={{
                width: `${Math.min(expansionAttainment, 100)}%`
              }} data-id="element-4940" />
              </div>
            </div>
            {/* Meetings Attainment */}
            <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-4941">
              <div className="flex items-center justify-between mb-2" data-id="element-4942">
                <span className="text-xs font-medium text-gray-600" data-id="element-4943">
                  Meetings attainment
                </span>
                <UsersIcon className="w-4 h-4 text-purple-600" data-id="element-4944" />
              </div>
              <div className="mb-2" data-id="element-4945">
                <span className="text-3xl font-bold text-gray-900" data-id="element-4946">
                  {meetingsAttainment.toFixed(0)}%
                </span>
              </div>
              <div className="text-xs text-gray-600" data-id="element-4947">
                {totalMeetings} of {targets.meetings} target
              </div>
              <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-4948">
                <div className="h-full bg-gradient-to-r from-purple-500 to-purple-600 rounded-full" style={{
                width: `${Math.min(meetingsAttainment, 100)}%`
              }} data-id="element-4949" />
              </div>
            </div>
            {/* Programs On Pace */}
            <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-4950">
              <div className="flex items-center justify-between mb-2" data-id="element-4951">
                <span className="text-xs font-medium text-gray-600" data-id="element-4952">
                  Programs on pace
                </span>
                <ActivityIcon className="w-4 h-4 text-orange-600" data-id="element-4953" />
              </div>
              <div className="mb-2" data-id="element-4954">
                <span className="text-3xl font-bold text-gray-900" data-id="element-4955">
                  {programsOnPace} of 15
                </span>
              </div>
              <button onClick={() => navigate('/programs')} className="text-xs text-blue-600 hover:text-blue-700 hover:underline" data-id="element-4956">
                {15 - programsOnPace} programs need attention →
              </button>
            </div>
          </div>
          {/* Trend Sparklines */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-4957">
            <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-4958">
              Activity Trends (14d)
            </h3>
            {/* Meetings Created */}
            <div className="mb-4" data-id="element-4959">
              <div className="flex items-center justify-between mb-2" data-id="element-4960">
                <span className="text-xs text-gray-600" data-id="element-4961">Meetings created</span>
                <span className="text-sm font-semibold text-gray-900" data-id="element-4962">
                  {meetingsData[meetingsData.length - 1].value}/day
                </span>
              </div>
              <ResponsiveContainer width="100%" height={40} data-id="element-4963">
                <LineChart data={meetingsData} data-id="element-4964">
                  <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={false} data-id="element-4965" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            {/* Orchestrations Run */}
            <div className="mb-4" data-id="element-4966">
              <div className="flex items-center justify-between mb-2" data-id="element-4967">
                <span className="text-xs text-gray-600" data-id="element-4968">
                  Orchestrations run
                </span>
                <span className="text-sm font-semibold text-gray-900" data-id="element-4969">
                  {orchestrationsData[orchestrationsData.length - 1].value}/day
                </span>
              </div>
              <ResponsiveContainer width="100%" height={40} data-id="element-4970">
                <LineChart data={orchestrationsData} data-id="element-4971">
                  <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} dot={false} data-id="element-4972" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            {/* AI Assisted Plays */}
            <div data-id="element-4973">
              <div className="flex items-center justify-between mb-2" data-id="element-4974">
                <span className="text-xs text-gray-600" data-id="element-4975">AI assisted plays</span>
                <span className="text-sm font-semibold text-gray-900" data-id="element-4976">
                  {aiPlaysData[aiPlaysData.length - 1].value}/day
                </span>
              </div>
              <ResponsiveContainer width="100%" height={40} data-id="element-4977">
                <LineChart data={aiPlaysData} data-id="element-4978">
                  <Line type="monotone" dataKey="value" stroke="#8b5cf6" strokeWidth={2} dot={false} data-id="element-4979" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          {/* Data Health */}
          <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-4980">
            <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-4981">
              Data health
            </h3>
            <div className="space-y-2" data-id="element-4982">
              <div className="flex items-center justify-between" data-id="element-4983">
                <span className="text-xs text-gray-600" data-id="element-4984">Integrations</span>
                <div className="flex items-center gap-1" data-id="element-4985">
                  <CheckCircleIcon className="w-4 h-4 text-green-600" data-id="element-4986" />
                  <span className="text-xs font-medium text-green-600" data-id="element-4987">OK</span>
                </div>
              </div>
              <div className="flex items-center justify-between" data-id="element-4988">
                <span className="text-xs text-gray-600" data-id="element-4989">Signals</span>
                <span className="text-xs text-gray-900" data-id="element-4990">Refreshed 2h ago</span>
              </div>
              <div className="flex items-center justify-between" data-id="element-4991">
                <span className="text-xs text-gray-600" data-id="element-4992">AI Agents</span>
                <span className="text-xs text-gray-900" data-id="element-4993">8 of 10 available</span>
              </div>
            </div>
            <button onClick={() => navigate('/integrations')} className="w-full mt-3 px-3 py-2 text-xs text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors" data-id="element-4994">
              View Configuration →
            </button>
          </div>
          {/* Levers to Close Gap */}
          <div className="bg-gradient-to-br from-red-600 to-orange-600 rounded-lg p-4 text-white" data-id="element-4995">
            <h3 className="text-sm font-semibold mb-3" data-id="element-4996">
              Levers to close the gap
            </h3>
            <p className="text-xs text-white/80 mb-3" data-id="element-4997">
              Top 3 by estimated impact
            </p>
            <div className="space-y-3" data-id="element-4998">
              {topLevers.map((lever, index) => <button key={index} onClick={lever.onClick} className="w-full text-left p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors" data-id="element-4999">
                  <div className="flex items-start gap-2" data-id="element-5000">
                    <ZapIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-5001" />
                    <div className="flex-1 min-w-0" data-id="element-5002">
                      <p className="text-xs font-medium mb-1" data-id="element-5003">
                        {lever.action}:{' '}
                        <span className="font-semibold" data-id="element-5004">{lever.name}</span>
                      </p>
                      <p className="text-xs text-white/80" data-id="element-5005">{lever.impact}</p>
                    </div>
                    <ChevronRightIcon className="w-4 h-4 flex-shrink-0" data-id="element-5006" />
                  </div>
                </button>)}
            </div>
          </div>
        </div>
      </div>
    </div>;
}